import { NavLink, useLocation } from "react-router";
import type { ReactNode } from "react";

import {
  ActivityIcon,
  BellIcon,
  ChartIcon,
  CompaniesIcon,
  KeyIcon,
  Logo,
  MapIcon,
  SearchIcon,
  ShieldIcon,
} from "../components/icons";
import { MyClaimsBlock } from "../features/claims/MyClaimsBlock";
import { StatusFilters } from "../features/companies/StatusFilters";
import { useSearch } from "../features/search/SearchProvider";
import { nl } from "../i18n/nl";
import styles from "./Sidebar.module.css";

export interface SidebarProps {
  className?: string;
  unreadCount?: number;
  /** Moderators see the "Beheer" section (role comes from `/me`, M1). */
  isModerator?: boolean;
  /** Open moderation flags, as a badge next to "Moderatie". */
  openFlagCount?: number;
}

function NavItem({
  to,
  icon,
  children,
  end,
  alsoActive = false,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  end?: boolean;
  /** Active on other paths too (e.g. a company page under "Bedrijven"). */
  alsoActive?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-current={alsoActive ? "page" : undefined}
      className={({ isActive }) =>
        [styles.item, (isActive || alsoActive) && styles.active].filter(Boolean).join(" ")
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

export function Sidebar({ className, unreadCount = 0, isModerator = false, openFlagCount = 0 }: SidebarProps) {
  const { pathname } = useLocation();
  const openSearch = useSearch();
  return (
    <aside className={[styles.sidebar, className].filter(Boolean).join(" ")}>
      <div className={styles.brand}>
        <Logo />
        <div>
          <div className={styles.name}>{nl.app.name}</div>
          <div className={styles.year}>{nl.app.yearLabel}</div>
        </div>
      </div>

      <button type="button" className={styles.search} aria-keyshortcuts="Control+K Meta+K" onClick={openSearch}>
        <SearchIcon size={15} />
        <span className={styles.searchLabel}>{nl.nav.search}</span>
        <kbd className={styles.kbd}>⌘K</kbd>
      </button>

      <nav aria-label={nl.nav.main} className={styles.nav}>
        <NavItem to="/" end alsoActive={pathname.startsWith("/bedrijven/")} icon={<CompaniesIcon />}>
          <span className={styles.label}>{nl.nav.companies}</span>
        </NavItem>
        <NavItem to="/kaart" icon={<MapIcon />}>
          <span className={styles.label}>{nl.nav.map}</span>
        </NavItem>
        <NavItem to="/activiteit" icon={<ActivityIcon />}>
          <span className={styles.label}>{nl.nav.activity}</span>
          {unreadCount > 0 && (
            <>
              <span className={styles.badge} aria-hidden="true">
                {unreadCount}
              </span>
              <span className="sr-only">{nl.nav.unread(unreadCount)}</span>
            </>
          )}
        </NavItem>
        <NavItem to="/gevolgd" icon={<BellIcon />}>
          <span className={styles.label}>{nl.nav.followed}</span>
        </NavItem>
      </nav>

      <div className={styles.nav}>
        <div className={styles.section}>{nl.nav.status}</div>
        <StatusFilters />
      </div>

      <MyClaimsBlock headingClassName={styles.section} />

      {isModerator && (
        <nav aria-label={nl.nav.admin} className={styles.nav}>
          <div className={styles.section}>{nl.nav.admin}</div>
          <NavItem to="/beheer/moderatie" icon={<ShieldIcon />}>
            <span className={styles.label}>{nl.nav.moderation}</span>
            {openFlagCount > 0 && (
              <>
                <span className={styles.badge} aria-hidden="true">
                  {openFlagCount}
                </span>
                <span className="sr-only">{nl.nav.toModerate(openFlagCount)}</span>
              </>
            )}
          </NavItem>
          <NavItem to="/beheer/statistieken" icon={<ChartIcon />}>
            <span className={styles.label}>{nl.nav.stats}</span>
          </NavItem>
          <NavItem to="/beheer/klascodes" icon={<KeyIcon />}>
            <span className={styles.label}>{nl.nav.inviteCodes}</span>
          </NavItem>
        </nav>
      )}
    </aside>
  );
}
